/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styles from './Fertilization.module.css';
import { ReactComponent as ICSI_selected } from '../../../assets/SVG/ICSI_selected.svg';
import { ReactComponent as IUI_selected } from '../../../assets/SVG/IUI_selected.svg';
import { ReactComponent as IVF_selected } from '../../../assets/SVG/IVF_selected.svg';
import { ReactComponent as ICSI_unselected } from '../../../assets/SVG/ICSI_unselected.svg';
import { ReactComponent as IUI_unselected } from '../../../assets/SVG/IUI_unselected.svg';
import { ReactComponent as IVF_unselected } from '../../../assets/SVG/IVF_unselected.svg';
import Grid from '@mui/material/Grid';
import { getEmbryoCulture, OocyteProcessing } from './FertilizationHelper';
import { useTranslation } from 'react-i18next';
import { safeConvertUnderscoreToTitle } from '../../../utils/commonUtils';

interface FertilizationProps {
  cycle: string;
}

const Fertilization: React.FC<FertilizationProps> = ({ cycle }) => {
  const [oocyteData, setOocyteData] = useState<OocyteProcessing[] | null>(null);
  const { t } = useTranslation('cycleOverview_config');

  useEffect(() => {
    getEmbryoCulture(cycle)
      .then((data) => {
        setOocyteData(data); 
      })
      .catch((error) => {
        console.error('Error fetching Embryo Culture Data:', error);
      });
  }, [cycle]);


  // Group by grade and action (e.g., { A: { Freezed: 2, Discard: 1 }, ... })
  const groupOocytesByGradeAndAction = (oocytes: any[]) => {
    return oocytes.reduce((acc: Record<string, Record<string, number>>, oocyte) => {
      const grade = oocyte.grade || 'Unknown';
      const disposition = oocyte.disposition || 'Unknown';
      if (!acc[grade]) acc[grade] = {};
      if (!acc[grade][disposition]) acc[grade][disposition] = 0;
      acc[grade][disposition]++;
      return acc;
    }, {});
  };


  // Helper: get selected technique
  const selectedTechnique = oocyteData?.[0]?.oocytes?.[0]?.fertilization_technique || null;

  // Map technique to selected/unselected image component
  const techniqueImages = [
    {
      key: 'ICSI',
      selected: ICSI_selected,
      unselected: ICSI_unselected,
    },
    {
      key: 'IUI',
      selected: IUI_selected,
      unselected: IUI_unselected,
    },
    {
      key: 'IVF',
      selected: IVF_selected,
      unselected: IVF_unselected,
    },
  ];

  return (
    <div className={styles.container}>
      <Grid container spacing={3}>
        {/* First column with images */}
        <Grid xs={12} sm={12} md={12} lg={12} xl={6}>
          <Grid container direction="row" spacing={2}>
            {techniqueImages.map(({ key, selected, unselected }) => {
              const ImgComponent = selectedTechnique === key ? selected : unselected;
              return (
                <Grid item xs={12} sm={6} md={12} lg={4} key={key} style={{ textAlign: 'center' }}>
                  <ImgComponent
                    style={{ width: '100%', height: 'auto', maxWidth: '12.5rem', objectFit: 'cover' }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>


        {/* Second column with data */}
        <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('TotalOocyteRetrieved')}</strong>
                {oocyteData &&
                  oocyteData.map((oocyte, index) => (
                    <p key={index} className={styles.customTextStyle}>{oocyte.oocyte_count}</p>
                  ))
                }
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('OocyteSource')}</strong>
                {oocyteData &&
                  oocyteData.map((oocyte, index) => (
                    <p key={index} className={styles.customTextStyle}>{'Self'}</p>
                  ))
                }
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('SpermSource')}</strong>
                {oocyteData && oocyteData.length > 0 && oocyteData[0].oocytes.length > 0 ? (
                  <p className={styles.customTextStyle}>
                    {oocyteData[0].oocytes[0]?.sperm_source || '-'}
                  </p>
                ) : (
                  <p className={styles.customTextStyle}>-</p>
                )}

              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('FertilizationTechnique')}</strong>
                {oocyteData && oocyteData.length > 0 && oocyteData[0].oocytes.length > 0 ? (
                  <p className={styles.customTextStyle}>
                    {oocyteData[0].oocytes[0]?.fertilization_technique || '-'}
                  </p>
                ) : (
                  <p className={styles.customTextStyle}>-</p>
                )}
              </div>
            </Grid>

          </Grid>
          <hr className='mt-4' />

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Grid container direction="row" >
              {oocyteData && oocyteData.length > 0 ? (
                oocyteData.map((data, index) => {
                  const grouped = groupOocytesByGradeAndAction(data.oocytes);

                  // Build action -> { grade: count } map
                  const actionGradeMap: Record<string, Record<string, number>> = {};
                  Object.entries(grouped).forEach(([grade, actions]) => {
                    Object.entries(actions).forEach(([disposition, count]) => {
                      if (!actionGradeMap[disposition]) actionGradeMap[disposition] = {};
                      actionGradeMap[disposition][grade] = count;
                    });
                  });
                  console.log(`Action Grade Map for index ${index}:`, actionGradeMap);
                    
                  return (
                    <React.Fragment key={index}>
                      {/* Render all disposition/grade boxes in a single row to avoid stacking for same disposition */}
                      <Grid container direction="row" spacing={2} justifyContent="flex-start" alignItems="flex-end">
                        {['MATURATION', 'FREEZE', 'DISCARD'].map((disposition) => {
                          const actionStyles: Record<string, {circle: string, box: string, border?: string}> = {
                            FREEZE: { circle: '#F8CFCF', box: '#FCFCFC' },
                            MATURATION: { circle: '#fff', box: '#FCFCFC',},
                            DISCARD: { circle: '#BDBDBD', box: '#FCFCFC' },
                          };
                          const style = actionStyles[disposition];
                          const grades = actionGradeMap[disposition];
                          if (!grades) return null;
                          return Object.entries(grades).map(([grade, count]) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={disposition + '-' + grade} style={{ display: 'flex',marginTop:25,  flexDirection: 'column', alignItems: 'center', minWidth: 180, width: '100%' }}>

                              <div style={{
                                background: style.box,
                                borderRadius: 16,
                                minWidth: 160,
                                minHeight: 80,
                                boxShadow: '2px 4px 4px 3px #F2F2F2',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                border: style.border || 'none',
                                position: 'relative',
                                paddingTop: 24,
                              }}>
                                <div style={{
                                  background: actionStyles[disposition]?.circle || '#eee',
                                  color: '#222',
                                  fontWeight: 700,
                                  borderRadius: '50%',
                                  width: 48,
                                  height: 48,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 18,
                                  position: 'absolute',
                                  top: -24,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  boxShadow: '0 4px 8px 0 #E0E0E0',
                                }}>{String(count).padStart(2, '0')}</div>
                                <div style={{ marginTop: 20, fontSize: 16, fontWeight: 500,  color: '#222', textAlign: 'center' }}>{grade === 'DEGENERATED'? t('Degenerated'):grade}</div>
                                <div style={{ marginTop: 8, fontSize: 14, color: '#888', textAlign: 'center' }}>{
                                  t(disposition) !== disposition ? t(disposition) : safeConvertUnderscoreToTitle(disposition)
                                }</div>
                              </div>
                            </Grid>
                          ));
                        })}
                      </Grid>
                    </React.Fragment>
                  );
                })
              ) : (
                <Grid item xs={12} style={{ textAlign: 'center', marginTop: 32 }}>
                  <p>{t('NoOocyteDataAvailable')}</p>
                </Grid>
              )}
              {/* Summary row */}
              <Grid item xs={12} style={{ marginTop: 32 }}>
                <Grid container direction="row" spacing={4} justifyContent="center" alignItems="center">
                  {['FREEZE', 'MATURATION', 'DISCARD'].map((action) => {
                    // Find total for each action
                    let total = 0;
                    oocyteData?.forEach((data) => {
                      data.oocytes.forEach((oocyte: any) => {
                        if ((oocyte.disposition || 'Unknown') === action) total++;
                      });
                    });
                    // Color for each action
                    const colorMap: Record<string, string> = {
                      FREEZE: '#F8CFCF',
                      MATURATION: '#fff',
                      DISCARD: '#BDBDBD',
                    };
                    const borderMap: Record<string, string> = {
                      MATURATION: '1.5px solid #E0E0E0',
                    };
                    return (
                      <Grid item key={action} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          display: 'inline-block',
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          background: colorMap[action] || '#eee',
                          border: borderMap[action] || 'none',
                          marginRight: 8,
                        }} />
                        <span style={{ color: '#BDBDBD', fontWeight: 500, fontSize: 18, marginRight: 8 }}>{t(action)}</span>
                        <span style={{ fontWeight: 700, fontSize: 18 }}>{String(total).padStart(2, '0')}</span>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
             
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Fertilization;
